import React, { useMemo, useState, useEffect } from "react";
import { Carousel } from "react-responsive-carousel";
import "react-responsive-carousel/lib/styles/carousel.min.css";
import { getSettings } from "./../services/settingsServices";
import { getAllGalleries } from "./../services/gallerieServices";

const BASE_URL = process.env.REACT_APP_API_URL || "http://localhost:3317";

const isExternalURL = (url) => {
  try {
    const newUrl = new URL(url);
    return newUrl.protocol === "http:" || newUrl.protocol === "https:";
  } catch (error) {
    return false;
  }
};

const absUrl = (u) => (isExternalURL(u) ? u : `${BASE_URL}${u}`);

const Diaporama = () => {
  const [photos, setPhotos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeIndex, setActiveIndex] = useState(0);

  useEffect(() => {
    const fetchGalleryPhotos = async () => {
      try {
        const settings = await getSettings();
        const carrouselId = settings?.[0]?.carrousel_id;

        if (!carrouselId) {
          setPhotos([]);
          setLoading(false);
          return;
        }

        const galleries = await getAllGalleries();
        const selectedGallery = galleries.find((g) => g.id === carrouselId);

        const activePhotos =
          selectedGallery?.photos?.filter((p) => p.is_active) || [];

        setPhotos(activePhotos);
      } catch (error) {
        console.error("Erreur lors de la récupération des photos:", error);
        setPhotos([]);
      } finally {
        setLoading(false);
      }
    };

    fetchGalleryPhotos();
  }, []);

  // Photos normalisées (url + alt)
  const items = useMemo(() => {
    return (photos || []).map((p) => ({
      id: p.id,
      url: absUrl(p.photo_url),
      alt: p.description || `Photo ${p.id}`,
      caption: p.description || "",
    }));
  }, [photos]);

  if (loading) {
    return (
      <div className="w-full">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-4">
          <div className="lg:col-span-9">
            <div className="h-[320px] sm:h-[420px] lg:h-[520px] rounded-3xl bg-black/5 dark:bg-white/10 animate-pulse" />
          </div>
          <div className="hidden lg:block lg:col-span-3">
            <div className="h-[520px] rounded-3xl bg-black/5 dark:bg-white/10 animate-pulse" />
          </div>
        </div>
      </div>
    );
  }

  if (items.length === 0) {
    return (
      <div className="rounded-3xl border border-black/10 dark:border-white/10 bg-white/40 dark:bg-white/5 p-6 text-center text-gray-700 dark:text-gray-300">
        Aucune photo disponible pour le carrousel
      </div>
    );
  }

  return (
    <section className="w-full">
      <div
        className="
          grid grid-cols-1 lg:grid-cols-12 gap-4
          items-stretch
        "
      >
        {/* Main carousel */}
        <div
          className="
            lg:col-span-9
            rounded-3xl overflow-hidden
            border border-black/10 dark:border-white/10
            bg-white/30 dark:bg-white/5
            shadow-sm
          "
        >
          <Carousel
            selectedItem={activeIndex}
            onChange={(i) => setActiveIndex(i)}
            autoPlay
            infiniteLoop
            stopOnHover
            swipeable
            emulateTouch
            interval={5000}
            transitionTime={500}
            useKeyboardArrows
            showThumbs={false}
            showIndicators
            showStatus={false}
            dynamicHeight={false}
            renderArrowPrev={(onClickHandler, hasPrev, label) =>
              hasPrev && (
                <button
                  type="button"
                  onClick={onClickHandler}
                  title={label}
                  className="
                    absolute left-3 top-1/2 -translate-y-1/2 z-20
                    w-11 h-11 rounded-2xl
                    bg-white/70 dark:bg-black/50 backdrop-blur-md
                    border border-black/10 dark:border-white/10
                    text-gray-900 dark:text-white
                    hover:bg-white/90 dark:hover:bg-black/70
                    transition
                  "
                  aria-label="Précédent"
                >
                  ‹
                </button>
              )
            }
            renderArrowNext={(onClickHandler, hasNext, label) =>
              hasNext && (
                <button
                  type="button"
                  onClick={onClickHandler}
                  title={label}
                  className="
                    absolute right-3 top-1/2 -translate-y-1/2 z-20
                    w-11 h-11 rounded-2xl
                    bg-white/70 dark:bg-black/50 backdrop-blur-md
                    border border-black/10 dark:border-white/10
                    text-gray-900 dark:text-white
                    hover:bg-white/90 dark:hover:bg-black/70
                    transition
                  "
                  aria-label="Suivant"
                >
                  ›
                </button>
              )
            }
          >
            {items.map((p) => (
              <div key={p.id} className="relative">
                <img
                  src={p.url}
                  alt={p.alt}
                  className="h-[320px] sm:h-[420px] lg:h-[520px] w-full object-cover"
                  loading="lazy"
                />

                {/* Caption (optionnel) */}
                {p.caption ? (
                  <div className="absolute inset-x-0 bottom-0 z-10 p-4">
                    <div
                      className="
                        inline-flex max-w-[92%]
                        rounded-2xl px-4 py-2
                        bg-black/45 text-white
                        backdrop-blur-md
                        border border-white/10
                        text-sm sm:text-base
                      "
                    >
                      {p.caption}
                    </div>
                  </div>
                ) : null}

                {/* Overlay léger */}
                <div className="absolute inset-0 pointer-events-none bg-gradient-to-t from-black/25 via-transparent to-transparent" />
              </div>
            ))}
          </Carousel>
        </div>

        {/* Thumbnails desktop (droite) */}
        <aside className="hidden lg:block lg:col-span-3">
          <div
            className="
              h-full rounded-3xl overflow-hidden
              border border-black/10 dark:border-white/10
              bg-white/30 dark:bg-white/5
              shadow-sm
            "
          >
            <div className="p-3">
              <div className="flex items-center justify-between px-2 pb-2">
                <div className="text-sm font-semibold text-gray-800 dark:text-gray-100">
                  Galerie
                </div>
                <div className="text-xs text-gray-600 dark:text-gray-400">
                  {activeIndex + 1}/{items.length}
                </div>
              </div>

              <div className="max-h-[520px] overflow-auto pr-1">
                <div className="grid grid-cols-1 gap-2">
                  {items.map((p, idx) => {
                    const active = idx === activeIndex;

                    return (
                      <button
                        key={p.id}
                        type="button"
                        onClick={() => setActiveIndex(idx)}
                        className={`
                          group w-full text-left
                          rounded-2xl overflow-hidden
                          border transition
                          ${
                            active
                              ? "border-primary ring-2 ring-primary/25"
                              : "border-black/10 dark:border-white/10 hover:border-primary/70"
                          }
                          bg-white/40 dark:bg-white/5
                        `}
                        aria-label={`Aller à la photo ${idx + 1}`}
                      >
                        <div className="flex gap-3 p-2">
                          <img
                            src={p.url}
                            alt={p.alt}
                            className="h-16 w-20 rounded-xl object-cover"
                            loading="lazy"
                          />
                          <div className="min-w-0 flex-1 py-0.5">
                            <div
                              className={`
                                text-sm font-semibold truncate
                                ${active ? "text-primary" : "text-gray-900 dark:text-gray-100"}
                              `}
                            >
                              {p.caption ? p.caption : `Photo ${idx + 1}`}
                            </div>
                            <div className="mt-1 text-xs text-gray-600 dark:text-gray-400">
                              Cliquer pour afficher
                            </div>
                          </div>

                          <div className="flex items-center">
                            <span
                              className={`
                                h-2 w-2 rounded-full transition
                                ${active ? "bg-primary" : "bg-black/20 dark:bg-white/20"}
                              `}
                            />
                          </div>
                        </div>
                      </button>
                    );
                  })}
                </div>
              </div>

              <div className="pt-3 px-2 text-xs text-gray-600 dark:text-gray-400">
                Astuce : utilisez les flèches du clavier ← →
              </div>
            </div>
          </div>
        </aside>
      </div>
    </section>
  );
};

export default Diaporama;
